import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { ArrowRight, Gamepad2, ExternalLink } from "lucide-react"
import type { Game } from "@/graphql/types"

const GAME_SKELETON_COUNT = 4

function formatearMilesManual(numero: number): string {
  return numero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

interface GamesSectionProps {
  games: Game[]
  loading: boolean
  error: unknown
}

export const GamesSection = ({ games, loading, error }: GamesSectionProps) => {
  const navigate = useNavigate()

  const skeletonCards = Array.from({ length: GAME_SKELETON_COUNT }, (_, i) => i + 1)

  const handleGameClick = (game: Game) => {
    if (game.urlActiveSeason) {
      navigate(game.urlActiveSeason)
    }
  }

  return (
    <section className="px-4 py-8 md:py-12">
      <div className="container mx-auto max-w-7xl px-2">
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-3xl font-bold md:text-4xl">Juegos Disponibles</h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Encuentra tu formato ideal, revisa torneos por juego y entra directo a la temporada
              activa.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate("/tournaments")}>
            Ver todos los eventos
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {loading || error ? (
          <>
            {error && (
              <div className="mb-4 text-center py-4">
                <p className="text-destructive">Error al cargar los juegos</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {skeletonCards.map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-44 bg-muted rounded-t-lg" />
                  <CardHeader>
                    <div className="h-6 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-full" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {games.map((game) => (
              <Card
                key={game.id}
                className={`group border-border/50 bg-card/70 backdrop-blur-sm transition-all duration-300 ${
                  game.urlActiveSeason
                    ? "cursor-pointer hover:border-primary/30 hover:shadow-lg hover:-translate-y-0.5"
                    : "opacity-85"
                }`}
                onClick={() => game.urlActiveSeason && handleGameClick(game)}
              >
                <div className="relative overflow-hidden rounded-t-lg border-b border-border/60">
                  <img
                    src={game.urlImage || "/placeholder.svg"}
                    alt={game.name}
                    className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
                  <div className="absolute left-3 top-3 flex items-center gap-2">
                    <Badge
                      className={
                        game.isActive
                          ? "bg-emerald-500/90 text-white"
                          : "bg-zinc-900/70 text-zinc-200"
                      }
                    >
                      {game.isActive ? "Activo" : "Inactivo"}
                    </Badge>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                    <span className="text-xs text-white/90">
                      {formatearMilesManual(game.totalTournaments || 0)} torneos
                    </span>
                  </div>
                </div>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg leading-tight group-hover:text-primary transition-colors">
                      {game.name}
                    </CardTitle>
                    <Gamepad2 className="h-5 w-5 text-primary/60 flex-shrink-0 ml-2" />
                  </div>
                  <CardDescription className="text-sm text-muted-foreground line-clamp-2">
                    {game.description || "Únete a los torneos de este emocionante juego de cartas"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  {game.urlActiveSeason && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleGameClick(game)
                      }}
                    >
                      Ver Temporada Activa
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {games.length === 0 && !loading && !error && (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 py-12 text-center">
            <Gamepad2 className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
            <p className="text-lg text-muted-foreground">No hay juegos activos disponibles</p>
          </div>
        )}
      </div>
    </section>
  )
}
